"use client";

import { useLiveQuery } from "@sanity/preview-kit";
import type { QueryParams } from "next-sanity";
import { type ReactElement, isValidElement, cloneElement } from "react";
import { applyTokens, type TokenMap } from "@marketing-sites/drlp";

interface SanityPreviewClientProps<T> {
  initialData: T;
  query: string;
  params?: QueryParams;
  children: ReactElement;
  dataPropName?: string;
  /**
   * Token map (e.g. {{resolutionRate}} → "76%") re-applied to each live-query
   * result. The live query refetches raw Sanity data, so without this the
   * server-side substitution would be lost on every preview update.
   */
  tokenMap?: TokenMap;
}

export function SanityPreviewClient<T>({
  initialData,
  query,
  params = {},
  children,
  dataPropName = "data",
  tokenMap,
}: SanityPreviewClientProps<T>) {
  const [data] = useLiveQuery<T>(initialData, query, params);
  const resolvedData = tokenMap ? applyTokens(data, tokenMap) : data;

  if (isValidElement(children)) {
    return cloneElement(children, { [dataPropName]: resolvedData } as object);
  }

  return children;
}
